if (!customElements.get('back-in-stock-notify')) {
  class BackInStockNotify extends HTMLElement {
    constructor() {
      super();
      this.onVariantChangeUnsubscriber = undefined;
      this.onModalClosed = undefined;
      this.onModalOpened = undefined;
      this.onSubmitClick = undefined;
      this.selectedVariant = null;
      this.isSubmitting = false;
    }

    connectedCallback() {
      this.opener = this.querySelector('[data-notify-opener]');
      this.openerWrapper = this.querySelector('.back-in-stock-notify__opener');
      this.productConfig = this.getProductConfig();
      this.selectedVariant = this.getSelectedVariant();

      this.productTitle = this.dataset.productTitle || this.productConfig?.title || '';
      this.variantSeparator = this.dataset.variantSeparator || ' · ';
      this.successTemplate = this.dataset.successTemplate || '';
      this.errorMessage = this.dataset.errorMessage || 'Unable to save your subscription. Please try again.';

      this.onModalOpened = this.handleModalOpened.bind(this);
      this.opener?.addEventListener('click', this.onModalOpened);

      this.onPickupMessageClick = () => this.openNotifyModal();
      this.pickupMessageOpener = document.querySelector('[data-back-in-stock-opener]');
      this.pickupMessageOpener?.addEventListener('click', this.onPickupMessageClick);

      this.onModalClosed = this.handleModalClosed.bind(this);
      document.body.addEventListener('modalClosed', this.onModalClosed);

      this.onSubmitClick = (event) => {
        event.preventDefault();
        this.handleSubmit();
      };

      this.onVariantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const sectionId = event?.data?.sectionId;
        if (sectionId && sectionId !== this.dataset.sectionId) return;

        const variant = event?.data?.variant;
        if (variant) {
          this.selectedVariant = variant;
          this.updateVariantInfo(variant);
          this.setNotifyVisible(!variant.available);
        }
      });

      if (this.selectedVariant) {
        this.updateVariantInfo(this.selectedVariant);
      }

      requestAnimationFrame(() => this.bindSubmitHandler());
    }

    disconnectedCallback() {
      this.opener?.removeEventListener('click', this.onModalOpened);
      this.pickupMessageOpener?.removeEventListener('click', this.onPickupMessageClick);
      this.onVariantChangeUnsubscriber?.();
      document.body.removeEventListener('modalClosed', this.onModalClosed);
      this.unbindSubmitHandler();
    }

    getProductConfig() {
      const configJson =
        this.querySelector('[data-back-in-stock-product]')?.textContent ||
        this.closest('product-info')?.querySelector('[data-back-in-stock-product]')?.textContent;

      if (!configJson) return null;

      try {
        return JSON.parse(configJson);
      } catch {
        return null;
      }
    }

    getModal() {
      return document.getElementById(this.dataset.modalId);
    }

    getModalElements() {
      const modal = this.getModal();
      if (!modal) return {};

      return {
        modal,
        formView: modal.querySelector('[data-notify-form-view]'),
        successView: modal.querySelector('[data-notify-success-view]'),
        emailInput: modal.querySelector('[data-notify-email]'),
        submitButton: modal.querySelector('[data-notify-submit]'),
        variantInfo: modal.querySelector('[data-notify-variant-info]'),
        successMessage: modal.querySelector('[data-notify-success-message]'),
        errorElement: modal.querySelector('[data-notify-error]'),
        consentInput: modal.querySelector('[data-notify-consent]'),
      };
    }

    bindSubmitHandler() {
      const { submitButton } = this.getModalElements();
      if (!submitButton || submitButton.dataset.notifyBound === 'true') return;

      submitButton.dataset.notifyBound = 'true';
      submitButton.addEventListener('click', this.onSubmitClick);
    }

    unbindSubmitHandler() {
      const { submitButton } = this.getModalElements();
      if (!submitButton || submitButton.dataset.notifyBound !== 'true') return;

      submitButton.removeEventListener('click', this.onSubmitClick);
      delete submitButton.dataset.notifyBound;
    }

    handleModalOpened() {
      this.resetToFormView();
      this.bindSubmitHandler();
    }

    openNotifyModal() {
      const modal = this.getModal();
      if (!modal) return;

      const variant = this.getSelectedVariant();
      if (variant) {
        this.updateVariantInfo(variant);
      }

      this.resetToFormView();
      this.bindSubmitHandler();
      modal.show(this.opener || this.pickupMessageOpener || this);
    }

    handleModalClosed() {
      this.resetToFormView();
    }

    resetToFormView() {
      const { formView, successView, emailInput, errorElement } = this.getModalElements();
      formView?.removeAttribute('hidden');
      successView?.setAttribute('hidden', '');
      emailInput?.closest('.custom-field')?.classList.remove('error');
      errorElement?.setAttribute('hidden', '');
      if (errorElement) errorElement.textContent = '';
    }

    showError(message) {
      const { errorElement } = this.getModalElements();
      if (!errorElement) return;

      errorElement.textContent = message;
      errorElement.removeAttribute('hidden');
    }

    async handleSubmit() {
      if (this.isSubmitting) return;

      const { emailInput, submitButton, consentInput } = this.getModalElements();
      const email = emailInput?.value?.trim();
      this.productConfig = this.getProductConfig() || this.productConfig;
      const variant = this.getSelectedVariant();

      if (!email || !this.isValidEmail(email)) {
        emailInput?.closest('.custom-field')?.classList.add('error');
        emailInput?.focus();
        return;
      }

      if (!window.KlaviyoCompositeCatalog) {
        this.showError('Back in stock scripts failed to load. Refresh the page and try again.');
        return;
      }

      if (!variant) {
        this.showError('Selected variant is unavailable. Choose a size or colour and try again.');
        return;
      }

      if (!this.productConfig?.title) {
        this.showError('Product details are unavailable. Refresh the page and try again.');
        return;
      }

      if (!window.backInStockSettings?.subscribeTaskId) {
        this.showError('Back in stock subscribe task is not configured.');
        return;
      }

      emailInput.closest('.custom-field')?.classList.remove('error');
      submitButton?.setAttribute('disabled', 'disabled');
      this.isSubmitting = true;

      try {
        const catalog = window.KlaviyoCompositeCatalog.buildCatalogPayload({
          productConfig: this.productConfig,
          variant,
        });

        await window.KlaviyoCompositeCatalog.subscribeViaDataJet({
          taskId: window.backInStockSettings.subscribeTaskId,
          email,
          marketingConsent: consentInput?.checked,
          catalog,
        });

        this.showSuccessView(email);
      } catch (error) {
        this.showError(error.message || this.errorMessage);
      } finally {
        this.isSubmitting = false;
        submitButton?.removeAttribute('disabled');
      }
    }

    showSuccessView(email) {
      const { formView, successView, successMessage } = this.getModalElements();
      const productDescription = this.getSuccessProductDescription();
      const message = this.successTemplate
        .replace('[[email]]', `<strong>${this.escapeHtml(email)}</strong>`)
        .replace('[[product]]', this.escapeHtml(productDescription));

      if (successMessage) {
        successMessage.innerHTML = message;
      }

      formView?.setAttribute('hidden', '');
      successView?.removeAttribute('hidden');
    }

    setNotifyVisible(visible) {
      const target = this.openerWrapper || this.opener;
      if (!target) return;

      if (visible) {
        target.removeAttribute('hidden');
      } else {
        target.setAttribute('hidden', '');
      }
    }

    parseVariantJson(rawValue) {
      if (!rawValue) return null;

      try {
        return JSON.parse(String(rawValue).trim());
      } catch {
        return null;
      }
    }

    getSelectedVariant() {
      if (this.selectedVariant) return this.selectedVariant;

      const sectionId = this.dataset.sectionId;
      const variantScript =
        document.getElementById(`variant-selects-${sectionId}`)?.querySelector('[data-selected-variant]') ||
        this.closest('product-info')?.querySelector('variant-selects [data-selected-variant]');

      const variantFromScript = this.parseVariantJson(variantScript?.innerHTML || variantScript?.textContent);
      if (variantFromScript) {
        this.selectedVariant = variantFromScript;
        return variantFromScript;
      }

      const productConfig = this.productConfig || this.getProductConfig();
      if (productConfig?.selected_variant) {
        this.selectedVariant = productConfig.selected_variant;
        return productConfig.selected_variant;
      }

      const variantId = this.closest('product-form')?.querySelector('input[name="id"]')?.value;
      if (variantId && productConfig?.selected_variant?.id === Number(variantId)) {
        this.selectedVariant = productConfig.selected_variant;
        return productConfig.selected_variant;
      }

      return null;
    }

    getVariantOptions(variant) {
      if (!variant) return [];

      return [variant.option1, variant.option2, variant.option3].filter(
        (option) => option && option !== 'Default Title'
      );
    }

    updateVariantInfo(variant) {
      const { variantInfo } = this.getModalElements();
      if (!variantInfo || !variant) return;

      const options = this.getVariantOptions(variant);
      const label = options.length
        ? [this.productTitle, ...options].join(this.variantSeparator)
        : this.productTitle;

      variantInfo.textContent = label;
    }

    getSuccessProductDescription() {
      const options = this.getVariantOptions(this.getSelectedVariant());

      if (!options.length) return this.productTitle;

      if (options.length === 1) {
        return `${this.productTitle} in ${options[0]}`;
      }

      const lastOption = options[options.length - 1];
      const otherOptions = options.slice(0, -1).join(', ');
      return `${this.productTitle} in ${otherOptions}, ${lastOption}`;
    }

    isValidEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }

    escapeHtml(value) {
      return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }
  }

  customElements.define('back-in-stock-notify', BackInStockNotify);
}
